import { useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import Button1 from "../../../components/form/Button1";
import ToggleSwitch from "../../../components/Settings/suppliers/ToggleSwitch";
import getSuppliersList from "../../../controllers/settings/supplier/getSuppliersList";
import Icon from "../../../components/HOC/Icon";

function normalizeList(list) {
  return Array.isArray(list) ? [...list] : [];
}

export default function SupplierTogglePicker({
  title,
  selected = [],
  onApply,
  onBack,
}) {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCodes, setSelectedCodes] = useState(normalizeList(selected));

  useEffect(() => {
    setSelectedCodes(normalizeList(selected));
  }, [selected]);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const res = await getSuppliersList();
      if (!active) return;
      setLoading(false);
      if (res?.return) {
        setSuppliers(res?.data || []);
      } else {
        enqueueSnackbar(res?.msg || "Failed to load suppliers", {
          variant: "error",
        });
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const selectedCount = selectedCodes.length;

  const sortedSuppliers = useMemo(() => {
    return [...suppliers].sort((a, b) => {
      const aSelected = selectedCodes.includes(a?.value);
      const bSelected = selectedCodes.includes(b?.value);
      if (aSelected === bSelected) {
        return String(a?.name || a?.value || "").localeCompare(
          String(b?.name || b?.value || ""),
        );
      }
      return aSelected ? -1 : 1;
    });
  }, [suppliers, selectedCodes]);

  function handleToggle(code, enabled) {
    setSelectedCodes((prev) => {
      if (enabled) {
        return prev.includes(code) ? prev : [...prev, code];
      }
      return prev.filter((item) => item !== code);
    });
  }

  return (
    <div className="flex flex-col gap-4 max-h-[70vh]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <span className="block text-sm font-medium text-gray-800">{title}</span>
          <span className="block text-xs text-gray-500 mt-1">
            {selectedCount} supplier{selectedCount === 1 ? "" : "s"} selected
          </span>
        </div>
        <button
          type="button"
          className="text-sm text-primary underline"
          onClick={() => setSelectedCodes([])}
        >
          Clear all
        </button>
      </div>

      <div className="flex-1 overflow-auto border rounded-md divide-y max-h-[50vh]">
        {loading ? (
          <div className="flex justify-center items-center py-10">
            <CircularProgress size={22} />
          </div>
        ) : sortedSuppliers.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-500">
            No suppliers found.
          </div>
        ) : (
          sortedSuppliers.map((supplier) => {
            const code = supplier?.value;
            const alias = supplier?.name || supplier?.alias || code;
            const enabled = selectedCodes.includes(code);
            return (
              <div
                key={code}
                className="flex items-center justify-between gap-3 px-3 py-3 hover:bg-gray-50"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {supplier?.icon ? (
                    <img
                      src={supplier.icon}
                      alt=""
                      className="w-8 h-8 object-contain rounded shrink-0"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded bg-[#F0F6FF] flex items-center justify-center shrink-0">
                      <Icon icon="mdi:airplane" className="text-lg text-primary" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <span className="block text-sm font-semibold text-gray-900 truncate">
                      {alias}
                    </span>
                    <span className="block text-xs text-gray-500 truncate">
                      {code}
                    </span>
                  </div>
                </div>
                <ToggleSwitch
                  toggleValue={enabled}
                  callback={(val) => handleToggle(code, val)}
                />
              </div>
            );
          })
        )}
      </div>

      <div className="flex gap-4 items-center pt-1">
        <div>
          <Button1 variant="outlined" onClick={() => onBack && onBack()}>
            Back
          </Button1>
        </div>
        <Button1 onClick={() => onApply && onApply(selectedCodes)}>
          Apply
        </Button1>
      </div>
    </div>
  );
}
